import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, ShoppingCart, Upload, FileCheck2, ChevronDown, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useApp } from "@/contexts/AppContext";
import { formatSAR } from "@/lib/i18n";
import { toast } from "sonner";

const STATUS_FLOW = ["pending", "processing", "done", "cancelled"];

export default function Orders() {
  const { t, lang } = useApp();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const inputsRef = useRef({});
  const [noteOrder, setNoteOrder] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    const r = await api.get("/orders");
    setOrders(r.data || []);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const onUpload = async (orderId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("zayd_token");
      const res = await fetch(`${API}/admin/orders/${orderId}/file`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error("failed");
      toast.success(lang === "ar" ? "تم رفع الملف" : "File uploaded");
      fetchAll();
    } catch (e) {
      toast.error("Upload failed");
    }
  };

  const setStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status });
      toast.success(lang === "ar" ? "تم التحديث" : "Updated");
      fetchAll();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const statusBadge = (status) => ({
    paid: "border-success/30 text-success bg-success/5",
    shipped: "border-primary/30 text-primary bg-accent",
    delivered: "border-success/30 text-success bg-success/10",
    pending: "border-warning/30 text-warning bg-warning/10",
    processing: "border-primary/30 text-primary bg-accent",
    done: "border-success/30 text-success bg-success/10",
    cancelled: "border-destructive/30 text-destructive bg-destructive/10",
    initiated: "border-muted-foreground/30 text-muted-foreground bg-muted",
  })[status] || "";

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t.dashboard}</p>
        <h1 className="text-2xl sm:text-3xl font-bold mt-1 flex items-center gap-2">
          <ShoppingCart className="h-6 w-6 text-primary" />
          {t.orders}
        </h1>
      </div>

      <div className="bg-surface border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <Th>{t.orderNumber}</Th>
              <Th>{t.billedTo}</Th>
              <Th>{t.total}</Th>
              <Th>{t.status}</Th>
              <Th>{lang === "ar" ? "تغيير الحالة" : "Set status"}</Th>
              <Th>{lang === "ar" ? "ملف" : "File"}</Th>
              <Th>{t.date}</Th>
              <Th className="text-end">{t.actions}</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">Loading...</td></tr>}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-muted-foreground" data-testid="no-orders">{lang === "ar" ? "لا يوجد طلبات" : "No orders yet"}</td></tr>
            )}
            {orders.map((o) => {
              const hasInfo = (o.items || []).some((it) => it.customer_info);
              return (
                <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-t border-border hover:bg-muted/30">
                  <Td className="font-mono font-semibold">
                    {o.order_number}
                    {hasInfo && (
                      <Badge variant="outline" className="ms-2 text-[9px] border-primary/40 text-primary">
                        {lang === "ar" ? "نكتب لك" : "Write-for-me"}
                      </Badge>
                    )}
                  </Td>
                  <Td>
                    <div className="font-medium">{o.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{o.customer_mobile}</div>
                  </Td>
                  <Td className="font-semibold">{formatSAR(o.total, lang)}</Td>
                  <Td><Badge variant="outline" className={statusBadge(o.status)}>{o.status}</Badge></Td>
                  <Td>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-1" data-testid={`status-trigger-${o.id}`}>
                          {lang === "ar" ? "تغيير" : "Change"}
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {STATUS_FLOW.map((s) => (
                          <DropdownMenuItem
                            key={s}
                            onClick={() => setStatus(o.id, s)}
                            data-testid={`status-set-${s}-${o.id}`}
                            className={o.status === s ? "font-bold text-primary" : ""}
                          >
                            {s}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                  <Td>
                    <input
                      type="file"
                      ref={(el) => (inputsRef.current[o.id] = el)}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) onUpload(o.id, f);
                        e.target.value = "";
                      }}
                      className="hidden"
                      data-testid={`order-file-input-${o.id}`}
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant={o.custom_storage_path ? "outline" : "default"}
                        onClick={() => inputsRef.current[o.id]?.click()}
                        className="gap-1"
                        data-testid={`order-upload-${o.id}`}
                      >
                        <Upload className="h-3 w-3" />
                        {o.custom_storage_path ? t.replaceFile : t.uploadFile}
                      </Button>
                      {o.custom_storage_path && (
                        <span className="inline-flex items-center gap-1 text-xs text-success">
                          <FileCheck2 className="h-3 w-3" />
                          {o.custom_filename}
                        </span>
                      )}
                    </div>
                  </Td>
                  <Td className="text-xs text-muted-foreground">
                    {new Date(o.created_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB")}
                  </Td>
                  <Td className="text-end">
                    {hasInfo && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setNoteOrder(o)}
                        className="gap-1 me-2"
                        data-testid={`order-note-${o.id}`}
                      >
                        <FileText className="h-3.5 w-3.5" />
                        {lang === "ar" ? "اقرأ معلوماته" : "Read note"}
                      </Button>
                    )}
                    <Link to={`/admin/orders/${o.id}/invoice`}>
                      <Button size="sm" variant="outline" data-testid={`order-invoice-${o.id}`} className="gap-1">
                        <Eye className="h-3.5 w-3.5" />
                        {t.invoice}
                      </Button>
                    </Link>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Customer note dialog */}
      <Dialog open={!!noteOrder} onOpenChange={(o) => !o && setNoteOrder(null)}>
        <DialogContent className="max-w-lg" data-testid="customer-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              {lang === "ar" ? "معلومات العميل" : "Customer information"}
            </DialogTitle>
            <DialogDescription>
              {noteOrder?.order_number} · {noteOrder?.customer_name} · {noteOrder?.customer_mobile}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-auto">
            {(noteOrder?.items || []).filter((it) => it.customer_info).map((it, i) => (
              <div key={i} className="border border-border rounded-lg p-4 bg-accent/30">
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-1">
                  {lang === "ar" ? it.name_ar : it.name_en}
                  {it.variant_name_en && <span className="ms-2 font-normal normal-case text-[10px]">· {lang === "ar" ? it.variant_name_ar : it.variant_name_en}</span>}
                </p>
                <p
                  className="text-sm whitespace-pre-wrap leading-relaxed"
                  data-testid={`customer-note-text-${i}`}
                >
                  {it.customer_info}
                </p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-start font-medium text-muted-foreground py-3 px-4 text-xs uppercase tracking-wider ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => <td className={`py-3 px-4 align-middle ${className}`}>{children}</td>;
